import { formatMoney } from "../utils";
import { summarizeBookingSet, type BookingSet } from "../utils/bookingSets";

export interface BookingSetCardProps {
  /** A multi-booking set. Single-booking sets use the normal booking row. */
  set: BookingSet;
  /** Resolved facility display name for a cross-facility surface. */
  facilityName?: string;
}

export interface BookingSetCardDisplay {
  title: string;
  countLabel: string;
  context: string;
  totalLabel: string;
  state: string;
  detailHref: string;
  detailAccessibleLabel: string;
}

function whenRange(bookings: BookingSet["bookings"]): string {
  const starts = bookings
    .map((booking) => new Date(booking.start_time))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((left, right) => left.getTime() - right.getTime());
  if (starts.length === 0) return "";

  const first = starts[0];
  const last = starts[starts.length - 1];
  try {
    if (first.toDateString() === last.toDateString()) {
      const day = new Intl.DateTimeFormat("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }).format(first);
      const time = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
      return starts.length > 1
        ? `${day} · ${time.format(first)} – ${time.format(last)}`
        : `${day} · ${time.format(first)}`;
    }

    const date = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${date.format(first)} – ${date.format(last)}`;
  } catch {
    return "";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "confirmed":
      return "Confirmed";
    case "cancelled":
      return "Cancelled";
    case "completed":
      return "Completed";
    case "no_show":
    case "no-show":
      return "No-show";
    case "pending":
      return "Pending";
    default:
      return status;
  }
}

function setStatus(bookings: BookingSet["bookings"]): string {
  const count = bookings.length;
  const recovering = bookings.filter(
    (booking) => booking.cancellation_recovery_pending === true,
  ).length;
  if (recovering > 0) {
    return recovering === count
      ? "Cancellation processing"
      : `${recovering} of ${count} cancellation processing`;
  }

  const cancelled = bookings.filter(
    (booking) => booking.status === "cancelled",
  ).length;
  if (cancelled > 0) {
    return cancelled === count
      ? "Cancelled"
      : `${cancelled} of ${count} cancelled`;
  }

  const counts = new Map<string, number>();
  for (const booking of bookings) {
    const label = statusLabel(booking.status);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  if (counts.size === 1) return counts.keys().next().value ?? "";
  return [...counts.entries()]
    .map(([label, stateCount]) => `${stateCount} ${label.toLowerCase()}`)
    .join(" · ");
}

/** Truthful projection used by the Player-owned MUI booking-set renderer. */
export function bookingSetCardDisplay({
  set,
  facilityName,
}: BookingSetCardProps): BookingSetCardDisplay {
  const summary = summarizeBookingSet(set.bookings);
  const hasKnownTotal = set.bookings.every(
    (booking) => booking.amount_charged != null,
  );
  const total = hasKnownTotal ? summary.totalCents / 100 : null;
  const detailHref = `/book/${set.anchor.facility_id}/bookings/${set.anchor.booking_id}`;
  const count = set.bookings.length;
  const noun = count === summary.courtCount ? "courts" : "bookings";
  const countLabel = `${count} ${noun}`;
  const state = setStatus(set.bookings);
  const when = whenRange(set.bookings);
  const courtName = summary.courtName;
  const title = facilityName ?? courtName ?? "Reservation";
  const context = [facilityName ? courtName : null, when]
    .filter(Boolean)
    .join(" · ");

  return {
    title,
    countLabel,
    context,
    totalLabel: formatMoney(total, summary.currency),
    state,
    detailHref,
    detailAccessibleLabel: `View reservation${
      facilityName ? ` at ${facilityName}` : ""
    }${courtName ? ` on ${courtName}` : ""} — ${countLabel}${
      when ? `, ${when}` : ""
    }`,
  };
}
